import { Fragment, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import Loader from "../../components/Loader";
import { Tab, Transition } from "@headlessui/react";
import { useSelector } from "react-redux";
import { RootState } from "src/context/store";
import { Button, Modal } from "src/components";
import PurpleLeftArrowIcon from "../../assets/purple-left-arrow.svg";
import OrderDetailsTab from "../OrderDetailsTab/OrderDetailsTab";
import { OrderInfoHomeScreen } from "./OrderInfoHomeScreen";
import { getCounterByEmployee, getOrderDetailsByOrderCode } from "src/services/order-api";
import { ordActions } from "src/context/order-slice";
import OrderInfoFuid from "./OrderInfoFuid";
import OrderInfoInvoices from "./OrderInfoInvoices";
import OrderInfoApprovalHierarchy from "./OrderInfoApprovalHierarchy";
import OrderStatusTooltip from "../OrderDetailsTab/OrderStatusTooltip";
import { getOrderInfoHomeScreenData } from "../OrderDetailsTab/OrderHelper";
import ChangeOrderStatus from "./OrderInfoTabCards/ChangeOrderStatus";
import Comments from "../OrderDetailsTab/Comments";
import { useOutsideClick } from "src/hooks/useOutsideClick";
import { MessageCircle } from "lucide-react";

const tabs = ["Order Info", "Order Details", "FUID", "Invoices", "Approval Hierarchy"];

const OrderInfoScreen = () => {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isChangeOrderStatusModalOpen, setIsChangeOrderStatusModalOpen] = useState(false);
  const { selectedCounterData, orderDetailsFiltered } = useSelector((state: RootState) => state.ord);
  const [isFiltersShown, setIsFiltersShown] = useState<boolean>(false);
  const { tabIndex, orderCode } = useParams();

  const ref = useOutsideClick(() => {
    setIsFiltersShown(false);
  });

  const fetchOrderId = async (orderCode: string) => {
    setIsLoading(true);
    const res = await getOrderDetailsByOrderCode(orderCode.toUpperCase());
    dispatch(ordActions.setOrderDetailsFiltered([res?.data?.data]));
    setIsLoading(false);
  };

  const fetchCounterData = async (counter_company_location_id: number) => {
    setIsLoading(true);
    const query = `?counter_company_location_id=${counter_company_location_id}&stockist=true`;

    try {
      const response = await getCounterByEmployee(query);
      dispatch(ordActions.setSelectedCounterData(response?.data?.data?.[0]));
    } catch (err) {
      console.log(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    dispatch(ordActions.setProductsData([]));
    dispatch(ordActions.clearCartDataObj());

    if (!selectedCounterData || Object.keys(selectedCounterData)?.length === 0) {
      fetchCounterData(Number(window.sessionStorage.getItem("counterCompanyLocationId")));
    }
  }, []);

  useEffect(() => {
    if (selectedCounterData && Object.keys(selectedCounterData)?.length > 0) {
      fetchOrderId(orderCode as string);
    }
  }, [selectedCounterData?.company_code]);

  return (
    <div>
      <div className="h-screen min-h-screen">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <Loader />
          </div>
        ) : (
          <div className="h-full bg-white">
            {/* Header Section */}
            {/* <div className="p-6 border-b border-purple-200 bg-gradient-to-r from-purple-50 to-purple-100"> */}
            <div className="mx-16 mt-6">
              <div className="container">
                <Button className="flex items-center px-0 py-2 text-lg text-[#7F56D9]" onClick={() => navigate("/transactions/orders")}>
                  <img src={PurpleLeftArrowIcon} alt="back" className="w-3 h-3 mr-3" />
                  Back to Counters
                </Button>

                <div className="flex items-center justify-between">
                  <div>
                    <h1 className="text-[2rem] font-bold text-gray-800">{orderDetailsFiltered?.[0]?.order_code}</h1>
                    <Button onClick={() => setIsChangeOrderStatusModalOpen(true)}>
                      <OrderStatusTooltip
                        orderStatusCode={orderDetailsFiltered?.[0]?.orderStatusHdr?.order_status_id}
                        orderStatusText={orderDetailsFiltered?.[0]?.orderStatusHdr?.order_status_desc}
                      />
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            <Tab.Group defaultIndex={Number(tabIndex) ?? 0}>
              <Tab.List className="border-b-[1px] relative shadow-md border-b-[rgba(0, 0, 0, 0.10)] mb-[1.16rem] px-[3.75rem] mt-4">
                {tabs.map((item: string) => (
                  <Tab key={item} as={Fragment}>
                    {({ selected }: { selected: boolean }) => (
                      <div
                        className={`relative inline-block cursor-pointer -bottom-[1px] px-4 py-3 font-semibold text-sm  border-b-2  not-italic  hover:border-b-[#586AF5] hover:text-[#586af5] focus:outline-none ${
                          selected ? "text-[#586af5] border-b-[#586AF5]" : "text-[rgba(0,0,0,0.4)] border-b-transparent"
                        }`}
                      >
                        {item}
                      </div>
                    )}
                  </Tab>
                ))}
                <MessageCircle
                  onClick={() => setIsFiltersShown((prev) => !prev)}
                  className="text-gray-700 text-[24px] absolute  mt-[1.16rem] mr-[1.16rem] cursor-pointer top-0 right-0"
                />
              </Tab.List>

              <Tab.Panels className="mx-16 mt-8">
                <Tab.Panel>
                  <OrderInfoHomeScreen
                    fetchOrderDetails={() => fetchOrderId(orderCode as string)}
                    orderInfo={getOrderInfoHomeScreenData(orderDetailsFiltered)}
                  />
                </Tab.Panel>
                <Tab.Panel>
                  <OrderDetailsTab />
                </Tab.Panel>
                <Tab.Panel>
                  <OrderInfoFuid />
                </Tab.Panel>
                <Tab.Panel>
                  <OrderInfoInvoices />
                </Tab.Panel>
                <Tab.Panel>
                  <OrderInfoApprovalHierarchy />
                </Tab.Panel>
              </Tab.Panels>
            </Tab.Group>
          </div>
        )}
      </div>
      <Modal isOpen={isChangeOrderStatusModalOpen} isHeader={false} onCloseModal={() => setIsChangeOrderStatusModalOpen(false)}>
        <ChangeOrderStatus onCloseModal={() => setIsChangeOrderStatusModalOpen(false)} />
      </Modal>
      <Transition
        as={Fragment}
        show={isFiltersShown}
        enter="transition transition ease-in-out duration-200 transform"
        enterFrom="translate-x-full"
        enterTo="translate-x-0"
        leave="transition  ease-in-out duration-200 transform"
        leaveFrom="translate-x-0"
        leaveTo="translate-x-full"
      >
        <div className="fixed right-0 inset-0 top-0 z-[500]">
          <Comments ref={ref} order_id={Number(orderDetailsFiltered?.[0]?.order_id_int) as number} uid={orderDetailsFiltered?.[0]?.uid} />
        </div>
      </Transition>
    </div>
  );
};

export default OrderInfoScreen;
